//React
import { FunctionComponent, useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
//FHIR
import Client, { FhirResource } from "fhir-kit-client";
import { Bundle, Device, Endpoint } from "fhir/r5";
import { SimpleCode, ValueSetLoader } from "@fyrstain/fhir-front-library";
//Icons
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
//Components
import { Title } from "@fyrstain/fhir-front-library";
import { Button, Card, Form, InputGroup } from "react-bootstrap";
import PandoraPage from "../../components/PandoraPage/PandoraPage";
//Translation
import i18n from "i18next";

interface State {
    endpointId: string,
    // Device
    active: boolean,
    name: string,
    deviceType: string,
    deviceVersion: string,
    standard: string,
    standardVersion: string,
    // Endpoint
    endpointName: string,
    endpointStatus: string,
    endpointConnectionType: string,
    endpointEnvironmentType: string,
    endpointUrl: string,
    // ValueSet related
    versionsUrl: string,
    deviceTypes: SimpleCode[],
    standards: SimpleCode[],
    standardsVersions: SimpleCode[],
    endpointStatuses: SimpleCode[],
    endpointConnectionTypes: SimpleCode[],
    endpointEnvironmentTypes: SimpleCode[],
}

// TOOD see what we do when it's a modification of a System
const SystemRegistration: FunctionComponent = () => {

    /////////////////////////////////////
    //           Constants             //
    /////////////////////////////////////

    const systemTypesUrl = process.env.REACT_APP_VALUESET_SYSTEMTYPES_URL ?? 'http://fyrstain.com/pdt/ValueSet/systemTypes';
    const standardsUrl = process.env.REACT_APP_VALUESET_STANDARDCODES_URL ?? 'http://fyrstain.com/pdt/ValueSet/standardCodes';
    const endpointStatusUrl = process.env.REACT_APP_VALUESET_ENDPOINTSTATUS_URL ?? 'http://hl7.org/fhir/ValueSet/endpoint-status';
    const endpointConnectionTypeValueSetUrl = process.env.REACT_APP_VALUESET_ENDPOINTCONNECTIONTYPE_URL ?? 'http://fyrstain.com/pdt/ValueSet/endpoint-connection-type'
    const endpointEnvironmentsUrl = process.env.REACT_APP_VALUESET_ENDPOINTENVIRONMENTS_URL ?? 'http://hl7.org/fhir/ValueSet/endpoint-environment';
    const endpointConnectionTypeCodeSystemUrl = process.env.REACT_APP_CODESYSTEM_ENDPOINTCONNECTIONTYPE_URL ?? 'http://terminology.hl7.org/CodeSystem/endpoint-connection-type';
    const pandoraStandardsCodeSystemUrl = process.env.REACT_APP_CODESYSTEM_PANDORASTANDARDS ?? 'http://fyrstain.com/pdt/CodeSystem/PandoraStandards';
    const endpointEnvironmentsCodeSystemUrl = process.env.REACT_APP_CODESYSTEM_ENDPOINTENVIRONMENTS_URL ?? 'http://hl7.org/fhir/endpoint-environment';

    /////////////////////////////////////
    //             States              //
    /////////////////////////////////////

    const formRef = useRef<HTMLFormElement>(null);

    const [validated, setValidated] = useState(false);

    const [messages, setMessages] = useState({
        name: i18n.t('errormessage.requiredfield'),
        owner: i18n.t('errormessage.requiredfield'),
        deviceType: i18n.t('errormessage.requiredfield'),
        deviceVersion: i18n.t('errormessage.requiredfield'),
        standard: i18n.t('errormessage.requiredfield'),
        standardVersion: i18n.t('errormessage.requiredfield'),
        endpointName: i18n.t('errormessage.requiredfield'),
        endpointStatus: i18n.t('errormessage.requiredfield'),
        endpointConnectionType: i18n.t('errormessage.requiredfield'),
        endpointUrl: i18n.t('errormessage.requiredfield'),
    });

    const { systemId } = useParams();

    const [counter, forceUpdate] = useReducer(x => x + 1, 0);

    const [state, setState] = useState({
        endpointId: '' as string,
        // Device
        active: true as boolean,
        name: '' as string,
        deviceType: '' as string,
        deviceVersion: '' as string,
        standard: '' as string,
        standardVersion: '' as string,
        // Endpoint
        endpointName: '' as string,
        endpointStatus: '' as string,
        endpointConnectionType: '' as string,
        endpointEnvironmentType: '' as string,
        endpointUrl: '' as string,
        // ValueSet related
        versionsUrl: '' as string,
        deviceTypes: [] as SimpleCode[],
        standards: [] as SimpleCode[],
        standardsVersions: [] as SimpleCode[],
        endpointStatuses: [] as SimpleCode[],
        endpointConnectionTypes: [] as SimpleCode[],
        endpointEnvironmentTypes: [] as SimpleCode[],
    } as State);

    /////////////////////////////////////
    //             Client              //
    /////////////////////////////////////

    const fhirClient = new Client({
        baseUrl: process.env.REACT_APP_FHIR_URL ?? 'fhir'
    });

    const valueSetLoader = new ValueSetLoader(fhirClient);

    /////////////////////////////////////
    //           Navigation            //
    /////////////////////////////////////

    const navigate = useNavigate();

    /**
     * Navigate to the Systems list page.
     * 
     */
    const toSystemList = useCallback(() => {
        navigate("/Systems");
    }, [navigate]);

    /**
     * Navigate to the Error page.
     * 
     */
    const onError = useCallback(() => {
        navigate("/Error");
    }, [navigate]);

    /////////////////////////////////////
    //            Functions           //
    /////////////////////////////////////

    /**
     * Gets a SimpleCode from the given list based on its string representation.
     * 
     * @param simpleValueSet    the list of codes.
     * @param value             the string representation of the code.
     * @returns the SimpleCode.
     * 
     */
    function getCode(simpleValueSet: SimpleCode[], value: string | undefined): SimpleCode | undefined {
        if (!value || value === "undefined") {
            return undefined;
        }
        const splitValue = value.split('|');
        if (splitValue.length === 2) {
            return simpleValueSet.find(simpleCode => simpleCode.system === splitValue[0] && simpleCode.code === splitValue[1]);
        } else {
            return simpleValueSet.find(simpleCode => simpleCode.code === splitValue[0] || simpleCode.display === splitValue[0])
        }
    }

    /**
     * Set the value of the given field in the state.
     * 
     * @param event 
     * 
     */
    const handleChange = (event: { target: { name: string; value: string } }) => {
        const { name, value } = event.target;
        set(name, value);
        setMessages((prev) => ({
            ...prev,
            [name]: value ? "" : i18n.t('errormessage.requiredfield')
        }));
    };

    /**
     * Set the value of the given field in the state.
     * 
     * @param name 
     * @param value 
     * 
     */
    function set(name: string, value: any) {
        setState({ ...state, [name]: value })
    }

    /**
     * Validate the form and display the validation message if the form is not valid.
     * 
     */
    function onValidateForm() {
        const isFormValid = formRef.current?.checkValidity();
        if (!isFormValid) {
            setValidated(true);
        } else {
            setValidated(true);
            onValidate();
        }
    }

    /**
     * Validate the form and send the Device and Endpoint resources to the FHIR server.
     * 
     */
    async function onValidate() {
        const bundle = createBundle();
        fhirClient.transaction({ body: bundle as FhirResource & { type: "transaction" } })
            .then(response => {
                //TODO Check transactionResponse
                console.log(response);
                toSystemList();
            })
            .catch(error => {
                console.error(error)
                onError();
            });
    }

    /**
     * Create a Bundle containing the Device and Endpoint resources based on the current state.
     * 
     * @returns the Bundle containing the Device and Endpoint resources.
     * 
     */
    function createBundle(): Bundle {
        const bundle = { "resourceType": "Bundle" } as Bundle;
        bundle.type = 'transaction';
        bundle.entry = [];
        bundle.entry.push(
            {
                "fullUrl": "urn:uuid:1",
                "resource": createEndpoint(),
                "request": {
                    "method": systemId ? "PUT" : "POST",
                    "url": systemId ? "Endpoint/" + state.endpointId : "Endpoint"
                }
            },
            {
                "fullUrl": "urn:uuid:2",
                "resource": createDevice(),
                "request": {
                    "method": systemId ? "PUT" : "POST",
                    "url": systemId ? "Device/" + systemId : "Device"
                }
            }
        )
        return bundle;
    }

    /**
     * Create a Device resource based on the current state.
     * 
     * @returns the Device resource.
     */
    function createDevice(): Device {
        const device = { "resourceType": "Device" } as Device;
        device.id = systemId ?? "device";
        device.displayName = state.name;
        device.name = []
        device.name.push(
            {
                "type": "patient-reported-name",
                "value": state.name,
                "display": true
            }
        );
        device.status = state.active ? "active" : "inactive";
        const deviceTypeCode = getCode(state.deviceTypes, state.deviceType);
        if (deviceTypeCode) {
            device.type = [valueSetLoader.toCodeableConcept(deviceTypeCode)];
        }
        device.version = [];
        device.version.push(
            {
                "type": {
                    "coding": [{
                        "system": "urn:iso:std:iso:11073:10101",
                        "code": "531975",
                        "display": "Software revision"
                    }]
                },
                "value": state.deviceVersion
            }
        )
        const standardCode = getCode(state.standards, state.standard);
        const standardVersionCode = getCode(state.standardsVersions, state.standardVersion);
        if (standardCode && standardVersionCode) {
            device.version.push(
                {
                    "type": valueSetLoader.toCodeableConcept(standardCode),
                    "value": standardVersionCode?.display ?? ""
                }
            )
        }
        //TODO Better handling with multiple endpoints
        device.endpoint = [];
        device.endpoint.push(
            {
                "reference": systemId ? "Endpoint/" + state.endpointId : "urn:uuid:1"
            }
        )
        return device;
    }

    /**
     * Create an Endpoint resource based on the current state.
     * 
     * @returns the Endpoint resource.
     * 
     */
    function createEndpoint(): Endpoint {
        const endpoint = { "resourceType": "Endpoint" } as Endpoint;
        endpoint.id = systemId ? state.endpointId : "endpoint";
        const endpointStatusCode = getCode(state.endpointStatuses, state.endpointStatus);
        endpoint.status = endpointStatusCode?.code as typeof endpoint.status ?? 'off';
        const endpointConnectionTypeCode = getCode(state.endpointConnectionTypes, state.endpointConnectionType);
        if (endpointConnectionTypeCode) {
            endpoint.connectionType = [];
            endpoint.connectionType.push(valueSetLoader.toCodeableConcept(endpointConnectionTypeCode))
        }
        endpoint.name = state.endpointName;
        //TODO Endpoint description
        const endpointEnvironmentTypeCode = getCode(state.endpointEnvironmentTypes, state.endpointEnvironmentType);
        if (endpointEnvironmentTypeCode) {
            endpoint.environmentType = [];
            endpoint.environmentType.push(valueSetLoader.toCodeableConcept(endpointEnvironmentTypeCode));
        }
        //TODO Managing Organization
        endpoint.address = state.endpointUrl;
        return endpoint;
    }

    /////////////////////////////////////
    //          Page Loading           //
    /////////////////////////////////////

    const [loading, setLoading] = useState(false);

    /**
     * Load the page when the component is mounted.
     * 
     */
    useEffect(() => {
        loadPage();
    }, []);

    /**
     * Load the translations when the language is changed.
     * 
     */
    useEffect(() => {
        setMessages({
            name: i18n.t('errormessage.requiredfield'),
            owner: i18n.t('errormessage.requiredfield'),
            deviceType: i18n.t('errormessage.requiredfield'),
            deviceVersion: i18n.t('errormessage.requiredfield'),
            standard: i18n.t('errormessage.requiredfield'),
            standardVersion: i18n.t('errormessage.requiredfield'),
            endpointName: i18n.t('errormessage.requiredfield'),
            endpointStatus: i18n.t('errormessage.requiredfield'),
            endpointConnectionType: i18n.t('errormessage.requiredfield'),
            endpointUrl: i18n.t('errormessage.requiredfield'),
        });
    }, [i18n.language]);

    /**
     * Load the ValueSets used for select fields.
     */
    async function loadPage() {
        setLoading(true);
        const loaded = state;
        await loadValueSets(loaded);
        await loadSystemData(loaded);
        setState(loaded);
        setLoading(false);
    }

    /**
     * Load all ValueSet needed to the loading of the Page.
     * 
     * @param loaded the state that is being loaded.
     */
    async function loadValueSets(loaded: State) {
        try {
            loaded.standards = await valueSetLoader.searchValueSet(standardsUrl);
            loaded.deviceTypes = await valueSetLoader.searchValueSet(systemTypesUrl);
            loaded.endpointStatuses = await valueSetLoader.searchValueSet(endpointStatusUrl);
            loaded.endpointConnectionTypes = await valueSetLoader.searchValueSet(endpointConnectionTypeValueSetUrl);
            loaded.endpointEnvironmentTypes = await valueSetLoader.searchValueSet(endpointEnvironmentsUrl);
        } catch (error) {
            console.log(error);
            onError();
        }
    }

    /**
     * Load the Device and Enpoint resources to display if the systemId parameter is used.
     * 
     * @param loaded the state that is being loaded.
     * 
     */
    async function loadSystemData(loaded: State) {
        if (systemId) {
            try {
                const response = await fhirClient.search({
                    resourceType: 'Device',
                    searchParams: { "_id": systemId ?? '', "_include": "Device:endpoint" }
                })
                const bundle: Bundle = response as Bundle;
                if (bundle.entry) {
                    for (const entry of bundle.entry) {
                        if ('Device' === entry.resource?.resourceType) {
                            const device = entry.resource as Device;
                            loaded.active = device.status === "active";
                            loaded.name = device.displayName ?? "";

                            const systemTypeString = device.type?.filter(
                                type => type.coding?.find(coding => coding.system === systemTypesUrl)
                            ).map(
                                type => type.coding?.find(coding => coding.system === systemTypesUrl)
                            ).map(
                                coding => coding?.system + "|" + coding?.code
                            ).at(0);
                            loaded.deviceType = systemTypeString ?? "undefined";

                            const deviceVersionString = device.version?.filter(
                                version => version.type?.coding?.find(coding => coding.system === "urn:iso:std:iso:11073:10101" && coding.code === "531975")
                            ).map(
                                version => version.value
                            ).at(0);
                            loaded.deviceVersion = deviceVersionString ?? "";

                            const standardVersionElement = device.version?.filter(
                                version => version.type?.coding?.find(coding => coding.system === pandoraStandardsCodeSystemUrl)
                            ).at(0);

                            const standardString = standardVersionElement?.type?.coding?.filter(
                                coding => coding.system === pandoraStandardsCodeSystemUrl
                            ).map(
                                coding => coding?.system + "|" + coding?.code
                            ).at(0);

                            loaded.standard = standardString ?? "undefined";
                            const standardCode = getCode(loaded.standards, loaded.standard);
                            if (standardCode) {
                                loaded.versionsUrl = standardCode.property?.valueString ?? "";
                                try {
                                    loaded.standardsVersions = await valueSetLoader.searchValueSet(standardCode.property?.valueString ?? "");
                                } catch (error) {
                                    console.log(error);
                                    onError();
                                }
                            }

                            const standardVersionString = standardVersionElement?.value;
                            const standardVersionCode = getCode(loaded.standardsVersions, standardVersionString)
                            loaded.standardVersion = standardVersionCode
                                ? standardVersionCode.system + '|' + standardVersionCode.code
                                : "undefined";
                            console.log("loaded : " + loaded.standardVersion)
                        } else if ('Endpoint' === entry.resource?.resourceType) {
                            const endpoint = entry.resource as Endpoint;
                            loaded.endpointId = endpoint.id ?? "";

                            loaded.endpointName = endpoint.name ?? "";
                            loaded.endpointStatus = endpoint.status ? "http://hl7.org/fhir/endpoint-status|" + endpoint.status : "undefined";

                            const endpointConnectionTypeString = endpoint.connectionType?.filter(
                                connectionType => connectionType?.coding?.find(coding => coding.system === endpointConnectionTypeCodeSystemUrl)
                            ).map(
                                connectionType => connectionType?.coding?.find(coding => coding.system === endpointConnectionTypeCodeSystemUrl)
                            ).map(
                                coding => coding?.system + "|" + coding?.code
                            ).at(0);
                            loaded.endpointConnectionType = endpointConnectionTypeString ?? "undefined";

                            const endpointEnvironmentString = endpoint.environmentType?.filter(
                                environmentType => environmentType?.coding?.find(coding => coding.system === endpointEnvironmentsCodeSystemUrl)
                            ).map(
                                environmentType => environmentType?.coding?.find(coding => coding.system === endpointEnvironmentsCodeSystemUrl)
                            ).map(
                                coding => coding?.system + "|" + coding?.code
                            ).at(0);
                            loaded.endpointEnvironmentType = endpointEnvironmentString ?? "undefined";

                            loaded.endpointUrl = endpoint.address;
                        }
                    }
                }
                setMessages({
                    name: loaded.name ? "" : i18n.t('errormessage.requiredfield'),
                    owner: i18n.t('errormessage.requiredfield'),
                    deviceType: loaded.deviceType !== "undefined" ? "" : i18n.t('errormessage.requiredfield'),
                    deviceVersion: loaded.deviceVersion ? "" : i18n.t('errormessage.requiredfield'),
                    standard: loaded.standard !== "undefined" ? "" : i18n.t('errormessage.requiredfield'),
                    standardVersion: loaded.standardVersion !== "undefined" ? "" : i18n.t('errormessage.requiredfield'),
                    endpointName: loaded.endpointName ? "" : i18n.t('errormessage.requiredfield'),
                    endpointStatus: loaded.endpointStatus !== "undefined" ? "" : i18n.t('errormessage.requiredfield'),
                    endpointConnectionType: loaded.endpointConnectionType !== "undefined" ? "" : i18n.t('errormessage.requiredfield'),
                    endpointUrl: loaded.endpointUrl ? "" : i18n.t('errormessage.requiredfield'),
                });
            } catch (error) {
                console.log(error);
                onError();
            }
        }
    }

    /**
     * Reset the form to its initial state.
     * 
     */
    function onReset() {
        formRef.current?.reset();
        setValidated(false);
        setState((prevState) => ({
            ...prevState,
            endpointId: '' as string,
            // Device
            active: true as boolean,
            name: '' as string,
            deviceType: '' as string,
            deviceVersion: '' as string,
            standard: '' as string,
            standardVersion: '' as string,
            // Endpoint
            endpointName: '' as string,
            endpointStatus: '' as string,
            endpointConnectionType: '' as string,
            endpointEnvironmentType: '' as string,
            endpointUrl: '' as string,
            // ValueSet related
            versionsUrl: prevState.versionsUrl,
            deviceTypes: prevState.deviceTypes,
            standards: prevState.standards,
            standardsVersions: prevState.standardsVersions,
            endpointStatuses: prevState.endpointStatuses,
            endpointConnectionTypes: prevState.endpointConnectionTypes,
            endpointEnvironmentTypes: prevState.endpointEnvironmentTypes,
        }));
    };

    /**
     * Reset the versions select when the standard select is reset.
     * 
     */
    useEffect(() => {
        if (state.standard === "") {
            set("standardVersion", "");
        } else {
            const updatedState = state;
            const standardCode = getCode(state.standards, state.standard);
            if (standardCode) {
                updatedState.versionsUrl = standardCode.property?.valueString ?? "";
                try {
                    valueSetLoader.searchValueSet(standardCode.property?.valueString ?? "")
                        .then(result => {
                            updatedState.standardsVersions = result;
                            setState(updatedState);
                            forceUpdate();
                        });
                } catch (error) {
                    console.log(error);
                    onError();
                }
            }
        }
    }, [state.standard]);

    /////////////////////////////////////
    //          Page Content           //
    /////////////////////////////////////

    function getOption(code: SimpleCode) {
        return <option value={code.system + '|' + code.code}>{code.display ?? code.code}</option>;
    }

    return (
        <PandoraPage titleKey={systemId ? "title.systemedition" : "title.systemregistration"} loading={loading} needsLogin={true} >
            <Form
                className={`d-flex flex-wrap gap-4 ${validated ? 'was-validated' : ''}`}
                noValidate
                validated={validated}
                onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                    e.preventDefault();
                    onValidateForm();
                }}
                ref={formRef}
            >

                <Card>
                    <Card.Header>
                        <Title level={2} content={'Informations'} />
                        <Form>
                            <Form.Check
                                type="switch"
                                label={i18n.t('label.activatethesystem')}
                                checked={state.active}
                                onChange={e => set("active", !state.active)}
                            />
                        </Form>
                    </Card.Header>
                    <Card.Body>
                        <div className="d-flex flex-wrap gap-4 w-100">
                            <Form.Group className="md-flex-container">
                                <Form.Label className="fw-bold">
                                    {i18n.t('label.name')} *
                                </Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        required
                                        value={state.name}
                                        name="name"
                                        onChange={handleChange}
                                        isInvalid={validated && !!messages.name}
                                    />
                                </InputGroup>
                                <Form.Control.Feedback
                                    type="invalid"
                                    className={validated && !!messages.name ? 'd-block' : 'd-none'}
                                >
                                    {messages.name}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="md-flex-container">
                                <Form.Label className="fw-bold">
                                    {i18n.t('general.owner')}
                                </Form.Label>
                                <InputGroup >
                                    <Form.Control
                                        // TODO when the field will be activated passed to required
                                        // required
                                        disabled={true}
                                    />
                                    <Button
                                        variant="outline-secondary"
                                    >
                                        <FontAwesomeIcon
                                            icon={faMagnifyingGlass}
                                        />
                                    </Button>
                                    {/* <Form.Control.Feedback type="invalid">
                                        {messages.owner}
                                    </Form.Control.Feedback> */}
                                </InputGroup>
                            </Form.Group>
                            <Form.Group className="md-flex-container">
                                <Form.Label className="fw-bold">
                                    {i18n.t('label.devicetype')} *
                                </Form.Label>
                                <Form.Select
                                    required
                                    value={state.deviceType}
                                    name="deviceType"
                                    onChange={handleChange}
                                >
                                    <option value="">
                                        {i18n.t('placeholder.devicetype')}
                                    </option>
                                    {state.deviceTypes.map(deviceType => getOption(deviceType))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {messages.deviceType}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="md-flex-container">
                                <Form.Label className="fw-bold">
                                    {i18n.t('label.deviceversion')} *
                                </Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        required
                                        value={state.deviceVersion}
                                        name="deviceVersion"
                                        onChange={handleChange}
                                        isInvalid={validated && !!messages.deviceVersion}
                                    />
                                </InputGroup>
                                <Form.Control.Feedback
                                    type="invalid"
                                    className={validated && !!messages.deviceVersion ? 'd-block' : 'd-none'}
                                >
                                    {messages.deviceVersion}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="md-flex-container">
                                <Form.Label className="fw-bold">
                                    Standard *
                                </Form.Label>
                                <Form.Select
                                    required
                                    value={state.standard}
                                    name="standard"
                                    onChange={handleChange}
                                >
                                    <option value="">
                                        {i18n.t('placeholder.standard')}
                                    </option>
                                    {state.standards.map(standard => getOption(standard))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {messages.standard}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="md-flex-container">
                                <Form.Label className="fw-bold">
                                    {i18n.t('label.standardversion')} *
                                </Form.Label>
                                <Form.Select
                                    required
                                    disabled={state.standard === ""}
                                    value={state.standardVersion}
                                    name="standardVersion"
                                    onChange={handleChange}
                                >
                                    <option value="">
                                        {i18n.t('placeholder.standardversion')}
                                    </option>
                                    {state.standardsVersions.map(standardVersion => getOption(standardVersion))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {messages.standardVersion}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </div>
                    </Card.Body>
                </Card>

                <Card className="w-100">
                    <Card.Header>
                        <Title level={2} content={'EndPoint'} />
                    </Card.Header>
                    <Card.Body>
                        <div className="d-flex flex-wrap gap-4">
                            <Form.Group className="md-flex-container">
                                <Form.Label className="fw-bold">
                                    {i18n.t('label.name')} *
                                </Form.Label>
                                <InputGroup className="md-flex-container">
                                    <Form.Control
                                        required
                                        value={state.endpointName}
                                        name="endpointName"
                                        onChange={handleChange}
                                        isInvalid={validated && !!messages.endpointName}
                                    />
                                </InputGroup>
                                <Form.Control.Feedback
                                    type="invalid"
                                    className={validated && !!messages.endpointName ? 'd-block' : 'd-none'}
                                >
                                    {messages.endpointName}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="md-flex-container">
                                <Form.Label className="fw-bold">
                                    {i18n.t('label.status')} *
                                </Form.Label>
                                <Form.Select
                                    required
                                    className="w-100"
                                    onChange={handleChange}
                                    name="endpointStatus"
                                    value={state.endpointStatus}
                                >
                                    <option value="">
                                        {i18n.t('placeholder.status')}
                                    </option>
                                    {state.endpointStatuses.map(status => getOption(status))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {messages.endpointStatus}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="md-flex-container">
                                <Form.Label className="fw-bold">
                                    {i18n.t('label.connectiontype')} *
                                </Form.Label>
                                <Form.Select
                                    required
                                    className="w-100"
                                    onChange={handleChange}
                                    name="endpointConnectionType"
                                    value={state.endpointConnectionType}
                                >
                                    <option value="">
                                        {i18n.t('placeholder.connectiontype')}
                                    </option>
                                    {state.endpointConnectionTypes.map(type => getOption(type))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {messages.endpointConnectionType}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="md-flex-container">
                                <Form.Label className="fw-bold">
                                    {i18n.t('label.environmenttype')}
                                </Form.Label>
                                <Form.Select
                                    className="w-100"
                                    onChange={handleChange}
                                    name="endpointEnvironmentType"
                                    value={state.endpointEnvironmentType}
                                >
                                    <option value="">
                                        {i18n.t('placeholder.environmenttype')}
                                    </option>
                                    {state.endpointEnvironmentTypes.map(environment => getOption(environment))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="md-flex-container">
                                <Form.Label className="fw-bold">
                                    URL *
                                </Form.Label>
                                <InputGroup className="w-100">
                                    <Form.Control
                                        required
                                        placeholder='URL'
                                        value={state.endpointUrl}
                                        name="endpointUrl"
                                        onChange={handleChange}
                                        isInvalid={validated && !!messages.endpointUrl}
                                    />
                                </InputGroup>
                                <Form.Control.Feedback
                                    type="invalid"
                                    className={validated && !!messages.endpointUrl ? 'd-block' : 'd-none'}
                                >
                                    {messages.endpointUrl}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </div>
                    </Card.Body>
                </Card>

                <Button
                    className="button"
                    variant="primary"
                    onClick={onValidateForm}
                >
                    {i18n.t('button.validate')}
                </Button>
                <Button
                    className="button"
                    variant="secondary"
                    onClick={onReset}
                >
                    {i18n.t('button.reset')}
                </Button>
            </Form>
        </PandoraPage >
    );
};
export default SystemRegistration;

